import { useTheme } from '@approvalmax/theme';
import { Box, Divider, Flex, Grid, Progress, SimpleLayout, Text, useLayout } from '@approvalmax/ui/src/components';
import { selectors } from 'modules/common';
import { DocumentPreviewPanel } from 'modules/common/ui';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useMedia } from 'react-use';
import { ComponentErrorBoundary } from 'shared/components';
import { useGetQuoteForCustomerDecision, useGetQuoteForCustomerDecisionPdf } from 'shared/data/webApp/v1';

import { Loading } from './loading';
import { DecisionMadeSection, DecisionMakerForm, ErrorCard } from './resources/components';
import { useGetQuoteSearchParams } from './resources/hooks/useGetQuoteParams';
import { messages } from './resources/messages';
import { Main } from './resources/styles';

const Page = () => {
    useLayout({ containerMinSize: 'xxsmall' });

    const { companyId, requestId, code } = useGetQuoteSearchParams();

    const { data: quoteForCustomerDecision, error: errorGetQuoteForCustomerDecision } = useGetQuoteForCustomerDecision({
        query: {
            companyId,
            requestId,
            code,
        },
    });

    const { data: quoteForCustomerDecisionPdf, isFetching: isFetchingGetQuoteForCustomerDecisionPdf } =
        useGetQuoteForCustomerDecisionPdf({
            query: {
                companyId,
                requestId,
                code,
                inline: true,
            },
        });

    const isDecisionMade = typeof quoteForCustomerDecision?.data.isAccepted === 'boolean';

    const document = useMemo(
        () => ({
            id: requestId || '',
            name: 'CustomerDecision',
            fileType: selectors.types.FileType.Pdf,
            url: quoteForCustomerDecisionPdf ? URL.createObjectURL(quoteForCustomerDecisionPdf) : '',
        }),
        [quoteForCustomerDecisionPdf, requestId]
    );

    const { theme } = useTheme();
    const mediumViewport = useMedia(`(max-width: ${theme.container.breakpoint.medium})`);
    const xsmallViewport = useMedia(`(max-width: ${theme.container.breakpoint.xsmall})`);

    const dividerCompensation = mediumViewport ? '-24' : '-40';

    if (errorGetQuoteForCustomerDecision) {
        return (
            <SimpleLayout size='small'>
                <SimpleLayout.Content>
                    <ErrorCard />
                </SimpleLayout.Content>
            </SimpleLayout>
        );
    }

    return (
        <SimpleLayout>
            <Helmet title={messages.title} />

            <SimpleLayout.Content>
                {!quoteForCustomerDecision ? (
                    <Loading />
                ) : (
                    <Grid gridTemplateRows={`max-content ${xsmallViewport ? '' : 'max-content'} 1fr`} height='100%'>
                        <Flex
                            alignItems={xsmallViewport ? 'start' : 'center'}
                            direction={xsmallViewport ? 'column' : 'row'}
                            spacing={xsmallViewport ? '4' : '16'}
                            wrap={false}
                        >
                            <Text font='label' fontSize='small' color='midnight80' ellipsis={1}>
                                {messages.quoteInfo({
                                    quoteNumber: quoteForCustomerDecision.data.quoteNumber,
                                    companyName: quoteForCustomerDecision.data.contact.name,
                                })}
                            </Text>

                            <Text font='label' fontSize='small' fontWeight='bold' color='midnight80' wrap={false}>
                                {`${quoteForCustomerDecision.data.amount} ${quoteForCustomerDecision.data.currency}`}
                            </Text>
                        </Flex>

                        {!xsmallViewport && <Divider spacing={`16 ${dividerCompensation} 0`} />}

                        <Main>
                            <Box spacing={xsmallViewport ? '24 0' : '16 24 0 0'}>
                                <ComponentErrorBoundary>
                                    {isFetchingGetQuoteForCustomerDecisionPdf || !document.url ? (
                                        <Flex alignItems='center' justifyContent='center' height='100%'>
                                            <Progress shape='circle' />
                                        </Flex>
                                    ) : (
                                        <DocumentPreviewPanel document={document} />
                                    )}
                                </ComponentErrorBoundary>
                            </Box>

                            {!xsmallViewport && (
                                <Divider orientation='vertical' spacing={`0 0 ${dividerCompensation}`} />
                            )}

                            <Box spacing={xsmallViewport ? '0' : '16 0 0 24'}>
                                {isDecisionMade ? (
                                    <DecisionMadeSection quote={quoteForCustomerDecision.data} />
                                ) : (
                                    <DecisionMakerForm />
                                )}
                            </Box>
                        </Main>
                    </Grid>
                )}
            </SimpleLayout.Content>
        </SimpleLayout>
    );
};

export default Page;
